// @ts-nocheck
import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { action } from '@ember/object'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import { service } from '@ember/service'
import { KNOWN_EXPERIMENT_FLAGS } from 'customer-flows/services/trackers/posthog'
import type PosthogService from 'customer-flows/services/trackers/posthog'
import config from 'customer-flows/config/environment'

const VARIANTS = ['test', 'control']

export default class ExperimentInspectorComponent extends Component {
  @service('trackers/posthog') posthog: PosthogService
  @service intentionalNavigation: IntentionalNavigation

  @tracked reloadOnChange = true

  get isEnabled() {
    return this.posthog.isEnabled
  }

  get canEnableForLocalDev() {
    return !!config.devtools?.enabled
  }

  @action
  enableForLocalDev() {
    this.posthog.enableForLocalDev()
    this.reload()
  }

  reload() {
    this.intentionalNavigation.reload()
  }

  get experiments() {
    return KNOWN_EXPERIMENT_FLAGS.map((flag) => ({
      flag,
      assignedVariant: this.posthog.getAssignedVariant(flag)
    }))
  }

  isActiveVariant = (experiment: { assignedVariant: unknown }, variant: string) => experiment.assignedVariant === variant

  @action
  toggleReloadOnChange(event: Event) {
    this.reloadOnChange = (event.target as HTMLInputElement).checked
  }

  @action
  setVariant(flag: string, variant: string) {
    this.posthog.overrideVariant(flag, variant)
    if (this.reloadOnChange) this.reload()
  }

  @action
  clearOverrides() {
    this.posthog.clearVariantOverrides()
    if (this.reloadOnChange) this.reload()
  }

  <template>
    <div data-testid='experiment-inspector' class='flex h-full w-full flex-col overflow-auto p-4'>
      <div class='mb-3 flex items-center justify-between'>
        <h2 class='text-xbold-para-16 text-neutrals-400'>A/B Tests</h2>
        {{#if this.isEnabled}}
          <div class='flex items-center gap-3'>
            <label class='text-reg-para-12 flex cursor-pointer items-center gap-1 text-neutrals-300'>
              <input
                type='checkbox'
                checked={{this.reloadOnChange}}
                {{on 'change' this.toggleReloadOnChange}}
              />
              Reload on change
            </label>
            <button
              type='button'
              class='text-reg-para-12 cursor-pointer text-theme-300 hover:underline'
              {{on 'click' this.clearOverrides}}
            >Clear overrides</button>
          </div>
        {{/if}}
      </div>

      {{#unless this.isEnabled}}
        <p class='text-reg-para-14 text-neutrals-300 mb-3'>PostHog is not initialized.</p>

        {{#if this.canEnableForLocalDev}}
          <div class='rounded-3 border border-neutrals-100 bg-neutrals-50 flex flex-col gap-2 p-4'>
            <p class='text-reg-para-12 text-neutrals-300'>
              Resolve experiments locally without a PostHog project — no events are sent.
            </p>
            <button
              type='button'
              class='text-reg-para-14 rounded-2 cursor-pointer border border-theme-300 bg-theme-50 px-3 py-2 text-theme-300 transition-colors hover:bg-theme-100'
              {{on 'click' this.enableForLocalDev}}
            >Enable local resolution</button>
          </div>
        {{/if}}
      {{else}}
        <div class='flex flex-col gap-3'>
          {{#each this.experiments as |experiment|}}
            <div class='rounded-3 border border-neutrals-100 bg-neutrals-50 p-4'>
              <div class='mb-2 flex items-center justify-between'>
                <span class='text-bold-para-14 text-neutrals-400'>{{experiment.flag}}</span>
                <span class='text-reg-para-12 text-neutrals-300'>
                  {{if experiment.assignedVariant experiment.assignedVariant 'not enrolled'}}
                </span>
              </div>
              <div class='flex gap-2'>
                {{#each VARIANTS as |variant|}}
                  <button
                    type='button'
                    class='text-reg-para-12 rounded-2 cursor-pointer border px-3 py-1 transition-colors
                      {{if
                        (this.isActiveVariant experiment variant)
                        "border-theme-300 bg-theme-50 text-theme-300"
                        "border-neutrals-100 text-neutrals-300 hover:bg-neutrals-100"
                      }}'
                    {{on 'click' (fn this.setVariant experiment.flag variant)}}
                  >{{variant}}</button>
                {{/each}}
              </div>
            </div>
          {{/each}}
        </div>
      {{/unless}}
    </div>
  </template>
}
